import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { backupLogAPI } from '../api/backupLog';
import { toast } from 'react-toastify';
import './ConfigDetails.css';

const BackupConfigDetails = () => {
  const { configId } = useParams();
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [configId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const configRes = await backupConfigAPI.getAll(1, 1000);
      const foundConfig = configRes.data.find(c => c._id === configId);
      
      if (!foundConfig) {
        toast.error('Configuration not found');
        navigate('/backup-configs');
        return;
      }
      console.log(foundConfig);
      
      setConfig(foundConfig);

      // Fetch recent logs
      try {
        const logsRes = await backupLogAPI.getLogsByConfig(configId, 1, 5);
        setRecentLogs(logsRes.data);
      } catch (err) {
        // Logs might not exist yet
        setRecentLogs([]);
      }
    } catch (error) {
      toast.error('Failed to fetch configuration details');
      navigate('/backup-configs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete backup configuration "${config.configName}"?`)) {
      return;
    }

    try {
      await backupConfigAPI.delete(configId);
      toast.success('Configuration deleted successfully');
      navigate('/backup-configs');
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { class: 'success', icon: '✅', text: 'Success' },
      failed: { class: 'failed', icon: '❌', text: 'Failed' },
      in_progress: { class: 'in-progress', icon: '⏳', text: 'Running' },
      initiated: { class: 'initiated', icon: '🔄', text: 'Starting' },
      partial_success: { class: 'partial', icon: '⚠️', text: 'Partial' },
      pending: { class: 'pending', icon: '⏸️', text: 'Pending' }
    };
    return badges[status] || { class: 'unknown', icon: '❓', text: status };
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading configuration details...</div>;
  }

  if (!config) {
    return <div className="error">Configuration not found</div>;
  }

  const statusBadge = config.lastBackupStatus ? getStatusBadge(config.lastBackupStatus) : null;

  return (
    <div className="config-details-container">
      <div className="details-header">
        <div>
          <h1>{config.configName}</h1>
          <p className="subtitle">Backup Configuration Details</p>
        </div>
        <div className="header-actions">
          <Link to={`/backup-configs/edit/${configId}`} className="btn btn-primary">
            Edit
          </Link>
          <button onClick={handleDelete} className="btn btn-danger">
            Delete
          </button>
        </div>
      </div>

      <div className="details-grid">
        <div className="details-card">
          <h3>Basic Information</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="label">Configuration Name:</span>
              <span className="value">{config.configName}</span>
            </div>
            {config.description && (
              <div className="info-item">
                <span className="label">Description:</span>
                <span className="value">{config.description}</span>
              </div>
            )}
            <div className="info-item">
              <span className="label">Installation:</span>
              <span className="value">
                {config.installation?.installationName ? (
                  <Link to={`/installations/${config.installation.uniqueId}`}>
                    {config.installation.installationName}
                  </Link>
                ) : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Backup Path:</span>
              <span className="value mono">{config.backupPath || '/backups'}</span>
            </div>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`badge ${config.isActive ? 'badge-success' : 'badge-danger'}`}>
                {config.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Created:</span>
              <span className="value">{formatDate(config.createdAt)}</span>
            </div>
            <div className="info-item">
              <span className="label">Last Updated:</span>
              <span className="value">{formatDate(config.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="details-card">
          <h3>SQL Server Configuration</h3>
          <div className="info-list">
            <div className="info-item">
              <span className="label">Host:</span>
              <span className="value">{config.sqlServerConfig?.host || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Port:</span>
              <span className="value">{config.sqlServerConfig?.port || 1433}</span>
            </div>
            <div className="info-item">
              <span className="label">Username:</span>
              <span className="value">{config.sqlServerConfig?.username || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Database:</span>
              <span className="value">{config.sqlServerConfig?.database || 'N/A'}</span>
            </div>
            {config.sqlServerConfig?.instanceName && (
              <div className="info-item">
                <span className="label">Instance:</span>
                <span className="value">{config.sqlServerConfig.instanceName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="details-card">
          <h3>Schedules ({config.schedules?.length || 0})</h3>
          {config.schedules && config.schedules.length > 0 ? (
            <div className="items-list">
              {config.schedules.map(schedule => (
                <div key={schedule._id || schedule} className="item-card">
                  <h4>{schedule.name || 'Schedule'}</h4>
                  {schedule.frequency && (
                    <p>{schedule.frequency} at {schedule.time}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">No schedules configured</p>
          )}
        </div>

        <div className="details-card">
          <h3>SFTP Servers ({config.sftpServers?.length || 0})</h3>
          {config.sftpServers && config.sftpServers.length > 0 ? (
            <div className="items-list">
              {config.sftpServers.map(server => (
                <div key={server._id || server} className="item-card">
                  <h4>{server.name || 'SFTP Server'}</h4>
                  {server.host && (
                    <p>{server.host}:{server.port}</p>
                  )}
                  {server.priority && (
                    <p className="priority">Priority: {server.priority}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-text">No SFTP servers configured</p>
          )}
        </div>

        <div className="details-card full-width">
          <h3>Last Backup Status</h3>
          {config.lastBackupAt ? (
            <div className="backup-status">
              <div className="status-info">
                <span className="label">Last Backup:</span>
                <span className="value">{formatDate(config.lastBackupAt)}</span>
              </div>
              {statusBadge && (
                <div className="status-info">
                  <span className="label">Status:</span>
                  <span className={`badge badge-${statusBadge.class}`}>
                    {statusBadge.icon} {statusBadge.text}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="empty-text">No backups performed yet</p>
          )}
        </div>

        {recentLogs.length > 0 && (
          <div className="details-card full-width">
            <h3>Recent Backup Logs</h3>
            <div className="logs-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLogs.map(log => (
                    <tr key={log._id}>
                      <td>{formatDate(log.createdAt)}</td>
                      <td>
                        <span className={`badge badge-${getStatusBadge(log.status).class}`}>
                          {getStatusBadge(log.status).text}
                        </span>
                      </td>
                      <td>{log.duration ? `${log.duration}s` : 'N/A'}</td>
                      <td>
                        <Link to={`/backup-log/${log._id}`} className="btn-link">
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupConfigDetails;